import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, Lock, CheckCircle2, AlertCircle, Smartphone } from 'lucide-react';
import { supabase } from '../../lib/supabase';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

// Call the public function with the anon key only. The logged-in app client
// would attach a stale session JWT, and the gateway then rejects the link.
async function callAcceptInvite(body: Record<string, unknown>): Promise<{ ok: boolean; status: number; data: any }> {
  const res = await fetch(`${SUPABASE_URL}/functions/v1/accept-resource-invite`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
    },
    body: JSON.stringify(body),
  });
  let data: any = null;
  try { data = await res.json(); } catch { data = null; }
  return { ok: res.ok, status: res.status, data };
}

function inviteError(result: { ok: boolean; status: number; data: any }, fallback: string): string {
  const fromBody = result.data?.error || result.data?.message || result.data?.msg;
  if (typeof fromBody === 'string' && fromBody && !/UNAUTHORIZED|Missing authorization/i.test(fromBody)) return fromBody;
  if (result.status === 401) return 'This invite link could not be opened. Ask your admin to resend it.';
  return fallback;
}

const APP_STORE_URL = 'https://apps.apple.com/app/nexus-book-ai/id6758896840';

interface InviteInfo {
  businessName: string;
  resource: { first_name?: string; last_name?: string; email?: string; role?: string | null };
}

export default function ResourceInviteAccept() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState<InviteInfo | null>(null);
  const [invalidReason, setInvalidReason] = useState<string | null>(null);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const clean = String(token || '').trim().replace(/[^a-zA-Z0-9]/g, '');
        const result = await callAcceptInvite({ action: 'fetch', token: clean });
        console.log('[resource-invite] fetch result', { tokenLen: clean.length, status: result.status, data: result.data });
        if (!active) return;
        if (!result.ok || !result.data?.valid) {
          setInvalidReason(inviteError(result, 'This invite link is invalid or has expired.'));
        } else {
          setInvite({ businessName: result.data.businessName, resource: result.data.resource });
        }
      } catch {
        if (active) setInvalidReason('Could not load this invite. Please try again.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [token]);

  const submit = async () => {
    setError(null);
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setSubmitting(true);
    try {
      const clean = String(token || '').trim().replace(/[^a-zA-Z0-9]/g, '');
      const result = await callAcceptInvite({ action: 'accept', token: clean, password });
      if (!result.ok || !result.data?.success) {
        setError(inviteError(result, 'Could not complete signup. Please try again.'));
        setSubmitting(false);
        return;
      }
      // Sign them in on the web.
      const { error: signInErr } = await supabase.auth.signInWithPassword({ email: result.data.email, password });
      if (signInErr) {
        // Account created but auto sign-in failed — send them to login.
        setDone(true);
        setSubmitting(false);
        return;
      }
      setDone(true);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", system-ui, sans-serif' }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-5">
          <div className="text-3xl font-bold tracking-tight text-gray-900">Nexus</div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20 text-gray-400"><Loader2 className="h-7 w-7 animate-spin" /></div>
          ) : invalidReason ? (
            <div className="p-8 text-center">
              <AlertCircle className="h-10 w-10 text-amber-500 mx-auto mb-3" />
              <div className="text-[16px] font-semibold text-gray-900">Invite unavailable</div>
              <div className="text-[14px] text-gray-500 mt-1.5">{invalidReason}</div>
              <button onClick={() => navigate('/login')} className="mt-5 inline-flex px-4 py-2.5 rounded-xl bg-blue-600 text-white text-[14px] font-semibold hover:bg-blue-700">Go to login</button>
            </div>
          ) : done ? (
            // Post-signup: App Store handoff (one-time)
            <div className="p-8 text-center">
              <CheckCircle2 className="h-11 w-11 text-green-500 mx-auto mb-3" />
              <div className="text-[18px] font-bold text-gray-900">You're all set!</div>
              <div className="text-[14px] text-gray-500 mt-1.5">Your account is ready. Get the Nexus app for the best experience on the go.</div>
              <a href={APP_STORE_URL} target="_blank" rel="noreferrer" className="mt-5 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gray-900 text-white text-[14px] font-semibold hover:bg-black">
                <Smartphone className="h-4 w-4" /> Download the iOS app
              </a>
              <button onClick={() => navigate('/')} className="mt-2.5 w-full px-4 py-3 rounded-xl border border-gray-200 text-gray-700 text-[14px] font-medium hover:bg-gray-50">Continue on the web</button>
            </div>
          ) : (
            <>
              <div className="bg-gray-50 border-b border-gray-200 px-6 py-6 text-center">
                <div className="text-[13px] text-gray-500">You've been invited to</div>
                <div className="text-[20px] font-bold text-gray-900 mt-0.5">{invite?.businessName}</div>
              </div>
              <div className="p-6">
                <div className="rounded-xl bg-gray-50 border border-gray-200 p-4 mb-5">
                  <div className="text-[15px] font-semibold text-gray-900">{invite?.resource.first_name} {invite?.resource.last_name}</div>
                  <div className="text-[13px] text-gray-500 mt-0.5">{invite?.resource.email}</div>
                  {invite?.resource.role && <div className="text-[12px] text-blue-700 bg-blue-50 inline-block px-2 py-0.5 rounded-full mt-2">{invite.resource.role}</div>}
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">Create a password</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" className="w-full pl-9 pr-3 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" style={{ fontSize: '16px' }} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">Confirm password</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') submit(); }} placeholder="Re-enter password" className="w-full pl-9 pr-3 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" style={{ fontSize: '16px' }} />
                    </div>
                  </div>
                  {error && <div className="text-[13px] text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
                  <button onClick={submit} disabled={submitting} className="w-full inline-flex items-center justify-center px-4 py-3 rounded-xl bg-blue-600 text-white text-[15px] font-semibold hover:bg-blue-700 disabled:opacity-60">
                    {submitting ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Setting up…</> : 'Accept invite & create account'}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        <p className="text-center text-[12px] text-gray-400 mt-4">Sent by Nexus on behalf of {invite?.businessName || 'a business'}.</p>
      </div>
    </div>
  );
}
